import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils/formatCurrency';
import './FloorPlanTableMarker.css';

const getTableScale = (seats = 4) => Math.min(1.6, Math.max(0.7, 1 + (seats - 4) * 0.1));

function FloorPlanTableMarker({ table, orders, alerts, onClick, group }) {
  const { t } = useTranslation();

  const tableOrders = useMemo(() => {
    const groupTableNumbers = group ? group.tableNumbers : [String(table.tableNumber)];
    return orders.filter((o) => groupTableNumbers.includes(String(o.tableNumber)));
  }, [orders, table, group]);

  const hasUnpaidOrders = tableOrders.some((o) => o.status !== 'cancelled' && o.paymentStatus !== 'paid');
  const isOccupied = table.status === 'occupied' || !!table.currentSessionId || !!table.sessionId || hasUnpaidOrders;
  const readyCount = tableOrders.filter((o) => o.status === 'ready').length;
  const preparingCount = tableOrders.filter((o) => o.status === 'preparing').length;
  const pendingCount = tableOrders.filter((o) => o.status === 'pending').length;
  const inProgressCount = pendingCount + preparingCount;

  const activeOrders = tableOrders.filter((o) => o.status !== 'cancelled' && o.status !== 'delivered');
  const activeOrderCount = activeOrders.length;

  const totalAmount = tableOrders
    .filter((o) => o.status !== 'cancelled')
    .reduce((sum, o) => sum + (o.total || 0), 0);

  const hasBillRequest = alerts?.billRequest;
  const hasWaiterCall = alerts?.waiterCall;

  const firstOrderTime = tableOrders
    .filter((o) => o.status !== 'delivered' && o.status !== 'cancelled')
    .map((o) => new Date(o.createdAt).getTime())
    .sort((a, b) => a - b)[0];

  const elapsedMinutes = firstOrderTime
    ? Math.floor((Date.now() - firstOrderTime) / 60000)
    : 0;

  const getStatusClass = () => {
    if (!isOccupied) return 'available';
    if (hasBillRequest) return 'bill-requested';
    if (readyCount > 0) return 'has-ready';
    if (elapsedMinutes > 15 && inProgressCount > 0) return 'waiting-long';
    if (inProgressCount > 0) return 'in-process';
    return 'occupied-idle';
  };

  const statusClass = getStatusClass();

  const getShapeClass = () => {
    switch (table.shape) {
      case 'round': return 'fpm--round';
      case 'rect': return 'fpm--rect';
      default: return 'fpm--square';
    }
  };

  const scale = getTableScale(table.seats);

  const statusLabelMap = {
    'available': t('waiter.floorPlanMarker.statusAvailable'),
    'in-process': t('waiter.floorPlanMarker.statusInProcess'),
    'has-ready': t('waiter.floorPlanMarker.statusHasReady'),
    'waiting-long': t('waiter.floorPlanMarker.statusWaitingLong'),
    'bill-requested': t('waiter.floorPlanMarker.statusBillRequested'),
    'occupied-idle': t('waiter.floorPlanMarker.statusOccupiedIdle'),
  };

  const tooltipParts = [`${t('waiter.floorPlanMarker.table')} ${table.tableNumber}`];
  if (isOccupied && activeOrderCount > 0) {
    tooltipParts.push(t('waiter.floorPlanMarker.orderCount', { count: activeOrderCount }));
    if (totalAmount > 0) {
      tooltipParts.push(formatCurrency(totalAmount));
    }
    if (elapsedMinutes > 0) {
      tooltipParts.push(t('waiter.floorPlanMarker.elapsed', { minutes: elapsedMinutes }));
    }
  }
  const tooltip = tooltipParts.join(' · ');

  const ariaLabel = t('waiter.floorPlanMarker.ariaLabel', {
    tableNumber: table.tableNumber,
    status: statusLabelMap[statusClass] || statusClass,
    orders: activeOrderCount,
    amount: totalAmount > 0 ? formatCurrency(totalAmount) : formatCurrency(0),
  });

  return (
    <button
      className={`fpm fpm--${statusClass} ${getShapeClass()}`}
      style={{
        left: `${table.positionX}%`,
        top: `${table.positionY}%`,
        '--table-scale': scale,
        transform: `translate(-50%, -50%) scale(${scale})`,
      }}
      title={tooltip}
      aria-label={ariaLabel}
      onClick={() => onClick(table)}
    >
      <span className="fpm-number">{table.tableNumber}</span>
      {isOccupied && activeOrderCount > 0 && (
        <span className={`fpm-badge${readyCount > 0 ? ' fpm-badge--ready' : ' fpm-badge--active'}`}>
          {activeOrderCount}
        </span>
      )}
      {isOccupied && totalAmount > 0 && (
        <span className="fpm-amount">{formatCurrency(totalAmount)}</span>
      )}
      {(hasWaiterCall || hasBillRequest) && (
        <span className="fpm-alert">
          {hasWaiterCall && '🔔'}
          {hasBillRequest && '🧾'}
        </span>
      )}
    </button>
  );
}

export default FloorPlanTableMarker;
